import React from 'react';
import { AlertCircle } from 'lucide-react';

interface ConfirmDialogProps {
  isVisible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'info';
  icon?: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isVisible,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  icon
}) => {
  if (!isVisible) return null;

  const bgColor = type === 'warning' ? 'bg-orange-50' : 'bg-blue-50';
  const borderColor = type === 'warning' ? 'border-orange-200' : 'border-blue-200';
  const iconColor = type === 'warning' ? 'text-orange-600' : 'text-blue-600';
  const confirmBtnColor = type === 'warning' 
    ? 'bg-orange-600 hover:bg-orange-700' 
    : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 ease-out">
        {/* Header */}
        <div className={`${bgColor} ${borderColor} border-b p-6 rounded-t-lg`}>
          <div className="flex items-center">
            {icon || <AlertCircle className={`${iconColor} mr-3`} size={24} />}
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-700 text-sm leading-relaxed mb-6">
            {message}
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 ${confirmBtnColor} text-white rounded-md transition-colors font-medium text-sm`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;